import { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, query, where, getDocs, updateDoc, doc, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { X, Send, MessageCircle, Clock, CheckCircle, Reply } from 'lucide-react';
import { toast } from 'react-toastify';
import { SupportMessage } from '../../models/types';
import { format } from 'date-fns';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SupportModal = ({ isOpen, onClose }: SupportModalProps) => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [supportMessages, setSupportMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState('');

  // Fetch user's support messages when modal opens
  useEffect(() => {
    if (!isOpen || !currentUser) return;

    const fetchSupportMessages = async () => {
      try {
        setLoading(true);
        console.log('Fetching support messages for user:', currentUser.uid);
        
        const supportRef = collection(db, 'supportMessages');
        
        // Use simple query without orderBy to avoid index requirement
        const q = query(
          supportRef,
          where('userId', '==', currentUser.uid)
        );
        
        const querySnapshot = await getDocs(q);
        console.log('Found support messages:', querySnapshot.size);
        
        const messagesList: SupportMessage[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log('Support message data:', data);
          messagesList.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            answeredAt: data.answeredAt?.toDate()
          } as SupportMessage);
        });
        
        // Sort manually since we can't use orderBy without index
        messagesList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        
        console.log('Processed support messages:', messagesList.length);
        setSupportMessages(messagesList);
        
        // Mark unread responses as notified
        const unreadResponses = messagesList.filter(msg => msg.answered && !msg.userNotified);
        console.log('Unread responses to mark as notified:', unreadResponses.length);
        
        for (const msg of unreadResponses) {
          try {
            await updateDoc(doc(db, 'supportMessages', msg.id), {
              userNotified: true
            });
            console.log('Marked message as notified:', msg.id);
          } catch (error) {
            console.warn('Could not mark message as notified:', msg.id, error);
          }
        }
        
        // If there are answered messages, switch to history tab
        if (messagesList.some(msg => msg.answered)) {
          console.log('Switching to history tab due to answered messages');
          setActiveTab('history');
        }
      } catch (error) {
        console.error('Error fetching support messages:', error);
        toast.error('Error loading support messages. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSupportMessages();
  }, [isOpen, currentUser]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser || !userData || !message.trim()) return;

    try {
      setSending(true);
      
      const supportData = {
        userId: currentUser.uid,
        userRole: userData.role,
        userName: userData.username,
        userEmail: userData.email,
        message: message.trim(),
        answered: false,
        userNotified: false,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'supportMessages'), supportData);
      
      toast.success('Support message sent successfully! We will respond as soon as possible.');
      setMessage('');
      // Refresh messages and switch to history tab
      window.location.reload();
    } catch (error) {
      console.error('Error sending support message:', error);
      toast.error('Error sending support message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleReplySubmit = async (e: React.FormEvent, originalMessageId: string) => {
    e.preventDefault();
    
    if (!currentUser || !userData || !replyMessage.trim()) return;

    try {
      setSending(true);
      
      const replyData = {
        userId: currentUser.uid,
        userRole: userData.role,
        userName: userData.username,
        userEmail: userData.email,
        message: replyMessage.trim(),
        answered: false,
        userNotified: false,
        replyToMessageId: originalMessageId,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'supportMessages'), replyData);
      
      toast.success('Reply sent successfully!');
      setReplyMessage('');
      setReplyingTo(null);
      // Refresh messages
      window.location.reload();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Error sending reply. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <MessageCircle className="h-6 w-6 text-primary-600 mr-2" />
              <h2 className="text-xl font-semibold">Support Center</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setActiveTab('new')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'new'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              New Message
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors relative ${
                activeTab === 'history'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Message History
              {supportMessages.some(msg => msg.answered && !msg.userNotified) && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
              )}
            </button>
          </div>

          {/* New Message Tab */}
          {activeTab === 'new' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="supportMessage" className="block text-sm font-medium text-gray-700 mb-2">
                  How can we help you?
                </label>
                <textarea
                  id="supportMessage"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 resize-none"
                  placeholder="Describe your issue or question in detail..."
                  required
                />
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-500">Maximum 500 characters</span>
                  <span className={`${message.length > 450 ? 'text-red-500' : 'text-gray-500'}`}>
                    {message.length}/500
                  </span>
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> Our support team typically responds within 24 hours. 
                  You'll receive a notification when we reply.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={sending || !message.trim() || message.length > 500}
                >
                  {sending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Message History Tab */}
          {activeTab === 'history' && (
            <div>
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                </div>
              ) : supportMessages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No support messages found</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Messages you send will appear here along with admin responses
                  </p>
                  <button
                    onClick={() => setActiveTab('new')}
                    className="mt-2 text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Send your first message
                  </button>
                </div>
              ) : (
                <div className="space-y-4 max-h-80 overflow-y-auto">
                  <div className="text-sm text-gray-600 mb-4">
                    Found {supportMessages.length} message{supportMessages.length !== 1 ? 's' : ''}
                  </div>
                  {supportMessages.map((supportMsg) => (
                    <div
                      key={supportMsg.id}
                      className={`border rounded-lg p-4 ${
                        supportMsg.answered ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'
                      }`}
                    >
                      {/* Message Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div className={`p-1 rounded-full ${
                            supportMsg.answered ? 'bg-green-100' : 'bg-yellow-100'
                          }`}>
                            {supportMsg.answered ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <Clock className="h-4 w-4 text-yellow-600" />
                            )}
                          </div>
                          <span className="text-sm font-medium">
                            {format(supportMsg.createdAt, 'MMM d, yyyy HH:mm')}
                          </span>
                          {supportMsg.replyToMessageId && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                              Reply
                            </span>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          supportMsg.answered 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {supportMsg.answered ? 'Answered' : 'Pending'}
                        </span>
                      </div>

                      {/* User Message */}
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Your Message:</h4>
                        <div className="bg-white p-3 rounded border">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{supportMsg.message}</p>
                        </div>
                      </div>

                      {/* Admin Response */}
                      {supportMsg.answered && supportMsg.adminResponse && (
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-gray-700 mb-1">Support Team Response:</h4>
                          <div className="bg-primary-50 p-3 rounded border border-primary-200">
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{supportMsg.adminResponse}</p>
                            {supportMsg.answeredAt && (
                              <p className="text-xs text-gray-500 mt-2">
                                Responded on {format(supportMsg.answeredAt, 'MMM d, yyyy HH:mm')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Reply Section */}
                      {supportMsg.answered && (
                        <div>
                          {replyingTo === supportMsg.id ? (
                            <form onSubmit={(e) => handleReplySubmit(e, supportMsg.id)} className="space-y-3">
                              <textarea
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                rows={3}
                                maxLength={500}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 resize-none text-sm"
                                placeholder="Type your follow-up message..."
                                required
                              />
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-500">
                                  {replyMessage.length}/500 characters
                                </span>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setReplyingTo(null);
                                      setReplyMessage('');
                                    }}
                                    className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="submit"
                                    disabled={sending || !replyMessage.trim()}
                                    className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                                  >
                                    {sending ? 'Sending...' : 'Send Reply'}
                                  </button>
                                </div>
                              </div>
                            </form>
                          ) : (
                            <button
                              onClick={() => setReplyingTo(supportMsg.id)}
                              className="flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                            >
                              <Reply className="h-4 w-4 mr-1" />
                              Reply to this message
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-4 border-t">
                <button
                  onClick={() => setActiveTab('new')}
                  className="w-full mb-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  Send New Message
                </button>
                <button
                  onClick={onClose}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportModal;